import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { AnnouncementSettings, Category, ListingCard, SellerSummary } from '@/types/marketplace'
import type { PublicStats } from '@/lib/marketplace/stats'
import type { HeaderState } from '@/lib/community/owner-hub'

import Link from 'next/link'

import {
    ArrowRight,
    BadgeCheck,
    Building2,
    Eye,
    MapPin,
    Send,
    ShieldCheck,
    Store
} from 'lucide-react'

import { getPublicCategories } from '@/lib/marketplace/categories'
import { getFeaturedListings, getLatestListings } from '@/lib/marketplace/listings'
import { pickRanked } from '@/lib/marketplace/shuffle'
import { getFeaturedSellers } from '@/lib/marketplace/sellers'
import { getPublicStats } from '@/lib/marketplace/stats'
import { getAnnouncement, getBrand, getLegal } from '@/lib/db/settings'
import { getHeaderState } from '@/lib/community/owner-hub'
import { getBaseUrl } from '@/lib/marketplace/url'
import { formatMonthYear } from '@/lib/marketplace/format'
import { canonicalUrl } from '@/lib/seo/canonical'
import { jsonLdGraph, organizationNode, webSiteNode } from '@/lib/seo/jsonld'
import { SEO_COUNTRIES, countryPagePath } from '@/lib/seo/countries'
import { DIRECTORY_COUNTRIES } from '@/lib/seo/telegram-directory'
import { DirectoryEntryLink } from '@/components/directory/directory-entry-link'
import { getDirectoryOverview } from '@/lib/directory/repository'

import { Seo } from '@/components/generals/seo'
import { SiteShell, Section, SectionHeading } from '@/components/marketplace/site-shell'
import { ListingGrid } from '@/components/marketplace/listing-card'
import { FeaturedListings } from '@/components/featured-listings/featured-listings'
import { EmptyState } from '@/components/marketplace/empty-state'
import { MarketCategories } from '@/components/market/market-categories'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { Hero } from '@/components/home/hero'
import { HowItWorks } from '@/components/home/how-it-works'
import { SellCta } from '@/components/home/sell-cta'

interface HomeProps {
    announcement: AnnouncementSettings
    categories: Array<Category>
    featured: Array<ListingCard>
    latest: Array<ListingCard>
    sellers: Array<SellerSummary>
    stats: PublicStats
    tagline: string
    disclosure: string
    baseUrl: string
    header: HeaderState
    /** Directory countries that list at least one checked group right now. */
    directoryCountries: Array<string>
}

/** "A", "A and B", "A, B and C". */
const joinNames = (names: Array<string>): string =>
    names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`

/*
    THE FIRST POINT USED TO OVERCLAIM, IN TWO SEPARATE WAYS.

    It read "Every listing moderated / A person reviews each submission before it
    reaches the marketplace or the channel." A person reads every listing only in
    a marketplace whose owner chose manual review; one running on automatic
    publishing sends clean, unflagged listings straight out. And "the channel"
    described a single platform-wide Telegram that does not exist — publishing is
    per marketplace, through that community's own bot.

    What IS true of every listing on every marketplace is the checking: the
    platform safety rules and the marketplace's own rules are applied to all of
    them, and anything the rules do not clear is held for a person.
*/
const SAFETY_POINTS = [
    { icon: Eye, title: 'Every listing checked', body: 'Bazio applies its platform safety rules and the marketplace’s own rules to every listing, and holds anything higher-risk for a person.' },
    { icon: BadgeCheck, title: 'Seller profiles and history', body: 'See how long a seller has been here, what they have listed, and what they have closed.' },
    { icon: ShieldCheck, title: 'One tap to report', body: 'Anything that looks wrong can be reported without an account, and admins can suspend instantly.' }
]

const Home: FC<HomeProps> = ({
    announcement,
    categories,
    featured,
    latest,
    sellers,
    tagline,
    disclosure,
    baseUrl,
    header,
    directoryCountries
}): ReactNode => {
    return (
        <SiteShell
            showSearch={false}
            disclosure={disclosure}
            announcement={announcement}
            categories={categories}
            {...header}
        >
            {/*
              * The home page passes NO title, so it renders the site name on
              * its own — every other page renders "{page} - Bazio".
              *
              * It is also the one page that defines the platform as an entity:
              * `Organization` and `WebSite` are declared here with stable ids,
              * and every other page's structured data points back at them
              * rather than describing a new company of its own.
              */}
            <Seo
                description={tagline}
                canonical={canonicalUrl(baseUrl, '/')}
                baseUrl={baseUrl}
                jsonLd={jsonLdGraph([organizationNode(baseUrl), webSiteNode(baseUrl)])}
            />

            {/* ---------------- Hero ---------------- */}
            {/* The strip and navigation come from the shared header, so the
                hero itself starts at the headline. */}
            <Hero />

            {/* ---------------- How Bazio works ----------------
                Moved directly under the hero. The model is the thing a
                first-time visitor has to grasp, and it was previously six
                sections down, below three grids of listings. */}
            <Section id='how-it-works' className='border-y border-ink-900/15 py-12 sm:py-16'>
                <HowItWorks />
            </Section>

            {/* ---------------- Categories ---------------- */}
            <Section id='categories' className='py-12 sm:py-16'>
                <MarketCategories />
            </Section>

            {/* ---------------- Featured ---------------- */}
            {featured.length > 0 && (
                <Section id='featured' className='pb-12 sm:pb-16'>
                    <FeaturedListings listings={featured} />
                </Section>
            )}

            {/* ---------------- Latest ---------------- */}
            <Section id='latest' className='pb-12 sm:pb-16'>
                <SectionHeading
                    title='Latest listings'
                    description='The newest listings from marketplaces across Bazio.'
                    action={(
                        <Link
                            href='/browse?sort=newest'
                            className={cn(buttonVariants({ variant: 'ghost' }), 'h-9 text-sm')}
                        >
                            See all
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    )}
                />

                {latest.length === 0 ? (
                    <EmptyState
                        className='mt-6'
                        title='No live listings yet'
                        description='Bazio is ready and waiting. The first listing from a connected community will appear right here.'
                        action={(
                            <Link
                                href='/sell'
                                className={cn(buttonVariants(), 'h-11 px-5 font-semibold')}
                            >
                                <Store className='size-4' aria-hidden='true' />
                                Be the first to list
                            </Link>
                        )}
                    />
                ) : (
                    <ListingGrid listings={latest} className='mt-6' />
                )}
            </Section>

            {/* ---------------- Browse by country ----------------
                A compact entry into the country pages, from the one list in
                `@/lib/seo/countries`. Kept to a single row on purpose: the home
                page is a marketplace, not a directory of SEO pages. */}
            <Section id='countries' className='pb-12 sm:pb-16'>
                <nav
                    aria-labelledby='countries-heading'
                    className='mta-border flex flex-col gap-4 rounded-mta-xs bg-paper p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6'
                >
                    <h2 id='countries-heading' className='type-display text-xl text-foreground sm:text-2xl'>
                        Browse marketplaces by country
                    </h2>

                    <ul className='flex flex-wrap gap-2'>
                        {SEO_COUNTRIES.map((country) => (
                            <li key={country.code}>
                                <Link
                                    href={countryPagePath(country)}
                                    className='type-label mta-focus inline-flex min-h-11 items-center gap-2 rounded-[5px] border border-ink-900/15 bg-paper px-4 py-2 text-[11px] text-ink-900 transition-colors duration-[var(--dur-fast)] hover:border-ink-900 hover:bg-ink-900 hover:text-paper'
                                >
                                    <MapPin className='size-4' aria-hidden='true' />
                                    {country.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                {/* Secondary on purpose: lighter than every red action on the
                    page. Names only countries that list checked groups today,
                    so the line can never promise a directory page that is empty. */}
                <aside
                    aria-labelledby='telegram-marketplaces-heading'
                    className='mt-4 flex flex-col gap-4 rounded-mta-xs border border-ink-900/15 bg-canvas p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6'
                >
                    <div className='flex min-w-0 items-start gap-4'>
                        <span
                            aria-hidden='true'
                            className='flex size-10 shrink-0 items-center justify-center rounded-[5px] border border-ink-900/15 bg-paper text-brand-red'
                        >
                            <Send className='size-5' />
                        </span>

                        <div className='min-w-0'>
                            <p className='type-label text-[11px] text-text-muted'>
                                Telegram Marketplaces
                            </p>

                            <h3 id='telegram-marketplaces-heading' className='mt-1 text-base font-bold text-ink-900 sm:text-lg'>
                                Looking for marketplaces on Telegram?
                            </h3>

                            <p className='mt-1 text-sm leading-relaxed text-text-muted'>
                                Explore public Telegram buy & sell communities.
                                {directoryCountries.length > 0 && (
                                    <span className='hidden sm:inline'>
                                        {` Now listing checked groups in ${joinNames(directoryCountries)}.`}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>

                    <DirectoryEntryLink
                        source='homepage'
                        className='type-label mta-focus inline-flex min-h-11 w-full shrink-0 items-center justify-center gap-2 rounded-[5px] border border-ink-900 bg-paper px-4 py-2 text-[11px] text-ink-900 transition-colors duration-[var(--dur-fast)] hover:bg-ink-900 hover:text-paper sm:w-auto'
                    >
                        Explore Telegram Marketplaces
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </DirectoryEntryLink>
                </aside>
            </Section>

            {/* ---------------- Verified sellers ---------------- */}
            {sellers.length > 0 && (
                <Section id='sellers' className='pb-12 sm:pb-16'>
                    <div className='flex flex-wrap items-end justify-between gap-4 border-b border-ink-900/15 pb-5'>
                        <div className='max-w-2xl'>
                            <p className='type-eyebrow flex items-center gap-2.5 text-text-muted'>
                                <span aria-hidden='true' className='inline-block h-[3px] w-7 shrink-0 bg-brand-red' />
                                The people behind the listings
                            </p>

                            <h2 className='type-display mt-4 text-2xl text-ink-900 sm:text-3xl'>
                                Verified sellers
                            </h2>

                            <p className='mt-3 text-sm leading-relaxed text-text-muted'>
                                Members whose Telegram identity or business details Bazio has checked.
                            </p>
                        </div>

                        <Link
                            href='/browse?verified=1'
                            className={cn(buttonVariants({ variant: 'outline' }), 'type-label mta-focus mta-scroll-safe mta-border h-11 rounded-mta-xs bg-paper px-5 text-[11px] text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper')}
                        >
                            All verified
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </div>

                    <ul className='mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                        {sellers.map((seller) => (
                            <li key={seller.id}>
                                <Link
                                    href={seller.telegramUsername ? `/seller/${seller.telegramUsername}` : '/browse?verified=1'}
                                    className='mta-border mta-focus mta-scroll-safe group flex h-full items-start gap-4 rounded-mta-xs bg-paper p-5 transition-transform duration-[var(--dur-fast)] ease-[var(--ease-brand)] motion-safe:hover:-translate-y-0.5'
                                >
                                    <span
                                        aria-hidden='true'
                                        className='type-label flex size-11 shrink-0 items-center justify-center rounded-mta-xs bg-ink-900 text-base text-paper transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] group-hover:bg-brand-red group-hover:text-ink-900'
                                    >
                                        {seller.displayName.trim().charAt(0).toUpperCase() || 'M'}
                                    </span>

                                    <span className='min-w-0 flex-1'>
                                        <span className='type-label block truncate text-[13px] leading-none text-ink-900'>
                                            {seller.businessName || seller.displayName}
                                        </span>

                                        <span className='mt-2 block text-xs leading-relaxed text-text-muted'>
                                            {seller.activeListings === 1 ? '1 active listing' : `${seller.activeListings} active listings`}
                                            {' since '}
                                            {formatMonthYear(seller.createdAt)}
                                        </span>

                                        <span className='mt-3 flex flex-wrap gap-1.5'>
                                            {seller.isVerified && (
                                                <span
                                                    title='Telegram identity confirmed by Bazio. This is not a guarantee of any transaction.'
                                                    className='type-label mta-border inline-flex items-center gap-1 rounded-mta-xs bg-brand-red px-2 py-1 text-[10px] leading-none text-ink-900'
                                                >
                                                    <BadgeCheck className='size-3' aria-hidden='true' />
                                                    Verified
                                                </span>
                                            )}

                                            {seller.isBusiness && (
                                                <span className='type-label mta-border inline-flex items-center gap-1 rounded-mta-xs bg-surface-muted px-2 py-1 text-[10px] leading-none text-ink-900'>
                                                    <Building2 className='size-3' aria-hidden='true' />
                                                    Business
                                                </span>
                                            )}
                                        </span>
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {/* ---------------- Safety ---------------- */}
            <Section id='safety-preview' className='pb-12 sm:pb-16'>
                <div className='mta-border grid gap-8 rounded-mta-sm bg-paper p-6 sm:p-8 lg:grid-cols-[1.05fr_1fr] lg:gap-12 lg:p-10'>
                    <div>
                        <h2 className='type-eyebrow flex items-center gap-2.5 text-text-muted'>
                            <span aria-hidden='true' className='inline-block h-[3px] w-7 shrink-0 bg-brand-red' />
                            Trust and safety
                        </h2>

                        {/*
                            Not "a reviewed marketplace": that restates the
                            every-listing-is-read claim the platform cannot make
                            for every marketplace. Rules ARE applied to all of
                            them, which is the honest version of the same point.
                        */}
                        <p className='type-display mt-5 text-2xl text-ink-900 sm:text-3xl'>
                            Rules on every listing, not an open feed
                        </p>

                        <p className='mt-4 max-w-prose text-sm leading-relaxed text-text-muted'>
                            {disclosure}
                        </p>

                        <div className='mt-7 flex flex-wrap items-center gap-3'>
                            <Link
                                href='/safety'
                                className={cn(buttonVariants(), 'type-label mta-focus mta-scroll-safe h-12 rounded-mta-xs px-6 text-[11px]')}
                            >
                                <ShieldCheck className='size-4' aria-hidden='true' />
                                Read the safety guide
                            </Link>

                            <Link
                                href='/report'
                                className={cn(buttonVariants({ variant: 'outline' }), 'type-label mta-focus mta-scroll-safe mta-border h-12 rounded-mta-xs bg-paper px-6 text-[11px] text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper')}
                            >
                                Report a listing
                            </Link>
                        </div>
                    </div>

                    <ul className='grid gap-4 sm:grid-cols-2 lg:grid-cols-1'>
                        {SAFETY_POINTS.map(({ icon: Icon, title, body }) => (
                            <li
                                key={title}
                                className='mta-border flex h-full items-start gap-4 rounded-mta-xs bg-surface-muted p-5'
                            >
                                <span
                                    aria-hidden='true'
                                    className='mta-border inline-flex size-10 shrink-0 items-center justify-center rounded-mta-xs bg-brand-red'
                                >
                                    <Icon className='size-5 text-ink-900' />
                                </span>

                                <span className='min-w-0'>
                                    <span className='type-label block text-[11px] text-ink-900'>
                                        {title}
                                    </span>

                                    <span className='mt-2 block text-sm leading-relaxed text-text-muted'>
                                        {body}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </Section>

            {/* ---------------- Sell CTA ---------------- */}
            {/* One closing action for the page: list an item, or join the
                community. The old dark Telegram panel said the same thing a
                second time, so this plate replaces it. */}
            <Section className='pb-16 sm:pb-20'>
                <SellCta />
            </Section>
        </SiteShell>
    )
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async ({ req, res }) => {
    /*
     * No shared cache: the featured row is drawn fresh on every visit, so a
     * cached copy would hand every visitor the same five promoted listings.
     */
    res.setHeader('Cache-Control', 'private, no-store, max-age=0')

    const [categories, featuredPool, latest, sellers, stats, brand, legal, announcement, header, directory] = await Promise.all([
        getPublicCategories(),
        getFeaturedListings(24),
        getLatestListings(8),
        getFeaturedSellers(4),
        getPublicStats(),
        getBrand(),
        getLegal(),
        getAnnouncement(),
        getHeaderState(req),
        // Cached for a minute; a failure simply hides the link.
        getDirectoryOverview().catch(() => null)
    ])

    /*
     * Picked once, here on the server, so the order is stable through
     * hydration and every re-render. Shuffling in the component would
     * mismatch the server HTML.
     */
    const featured = pickRanked(featuredPool, 3, (listing) => listing.promotionPriority)

    return {
        props: {
            announcement,
            categories,
            featured,
            latest,
            sellers,
            stats,
            tagline: brand.tagline,
            disclosure: legal.notAPartyStatement,
            baseUrl: getBaseUrl(req),
            header,
            // The card itself always shows (the directory is public); this only
            // names the countries that list checked groups today.
            directoryCountries: directory
                ? DIRECTORY_COUNTRIES.filter((country) => directory.countryListed(country.code) > 0).map((country) => country.label)
                : []
        }
    }
}

export default Home
